import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { fetchVehicleAvailability, fetchVehicleAvailabilityWithTrimCab } from '../../services/VehicleAvailabilityService';
import { normalizeAvailability, buildBiz2Coverages } from '../../helpers/availabilityUtils';
import { useAppData, AppDataType } from '../../context/Contexts';
import sharedStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import stepNavStyles from '../StepNav/styles.module.scss';

interface Answer {
    key: string;
    label: string;
    sku: string;
    icon_url: string;
}

interface Question {
    id: number;
    sort_order: number;
    text: string;
    answers: Answer[];
}

interface PreKitEntry {
    sku: string;
    id: number;
}

interface SeatAvailability {
    prekit: Record<string, PreKitEntry> | [];
    [color: string]: boolean | Record<string, PreKitEntry> | [];
}

interface SkuMappingVideo {
    id: number;
    name: string;
    slug: string;
    video_url: string;
    hero_img_url?: string;
    hero_img_title?: string;
}

interface SkuMapping {
    id: number;
    conditions: Record<string, string>;
    bundle_code: string;
    availability: Record<string, SeatAvailability>;
    video?: SkuMappingVideo;
}

interface ConfiguratorConfig {
    id: number;
    name: string;
    description: string;
    questions: Question[];
    sku_mappings: SkuMapping[];
}

interface AvailabilityResponse {
    data: {
        configurator: ConfiguratorConfig[];
        availability?: Record<string, any>;
    };
}

const bumpIconVersion = (url: string) => url.replace(/(\?v=)(\d+)/, (_, p, v) => `${p}${Number(v) + 1}`);

const VehicleConfigurator: React.FC<{ stepNumber?: number }> = ({ stepNumber = 3 }) => {
    const { currentAppData, setCurrentAppData } = useAppData();
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    const [unlockedQuestions, setUnlockedQuestions] = useState<Set<number>>(new Set());
    const [showFrontsOnlyAlert, setShowFrontsOnlyAlert] = useState(false);
    // Stores the original check.compatibility before a "Fronts Only" bundle_code overrides it
    const originalCompatibilityRef = useRef<string | null>(null);

    const year = currentAppData?.year;
    const make = currentAppData?.make;
    const model = currentAppData?.model;
    const seat = currentAppData?.seat;

    // Questions only apply to Bundle / Rear — skip entirely for front-only selections
    const isFrontsOnly = !!(seat && (seat.includes('Front') || seat === 'First Row') && !seat.includes('Bundle') && !seat.includes('Rear'));

    const trim = currentAppData?.trim;
    const cab = currentAppData?.cab;

    const enabled = !!(year && make && model);
    const trimCabEnabled = enabled && !!(trim && cab);

    const queryKey = trimCabEnabled
        ? ['vehicle-availability', year, make, model, trim, cab]
        : ['vehicle-availability', year, make, model];

    const queryFn = trimCabEnabled
        ? () => fetchVehicleAvailabilityWithTrimCab(year!, make!, model!, trim!, cab!) as Promise<AvailabilityResponse>
        : () => fetchVehicleAvailability(year!, make!, model!) as Promise<AvailabilityResponse>;

    const { data, isLoading } = useQuery<AvailabilityResponse>({
        queryKey,
        queryFn,
        enabled,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    // Signal loading state so StepNav won't advance to trim/cab before we know the result
    useEffect(() => {
        if (!enabled) return;
        setCurrentAppData(prev => ({ ...prev, biz2_availability_loading: isLoading }));
    }, [isLoading, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    const configurator: ConfiguratorConfig[] = Array.isArray(data?.data?.configurator) ? data.data.configurator : [];
    const questions: Question[] = (configurator.length > 0 && Array.isArray(configurator[0]?.questions))
        ? [...configurator[0].questions].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        : [];

    // When fronts-only is selected, questions are irrelevant — mark complete immediately.
    // When switching back to bundle/rear and questions exist, restore pending state.
    useEffect(() => {
        if (!questions.length) return;
        if (isFrontsOnly) {
            // Dismiss any pending Fronts Only alert (user changed seat manually).
            setShowFrontsOnlyAlert(false);
            const firstMappingAvailability = configurator[0]?.sku_mappings?.[0]?.availability ?? null;
            const biz2CoveragesFromMapping = buildBiz2Coverages(firstMappingAvailability);
            setCurrentAppData((prev: AppDataType | null) => {
                const keepCoverages = prev?.check?.compatibility === 'F';
                console.log('[VehicleConfigurator] isFrontsOnly effect', {
                    seat: prev?.seat,
                    compatibility: prev?.check?.compatibility,
                    keepCoverages,
                    biz2_coverages_keys: prev?.biz2_coverages ? Object.keys(prev.biz2_coverages) : null,
                });
                return {
                    ...prev,
                    configurator_complete: true,
                    // Keep biz2_coverages only when compatibility is already 'F' — that means a
                    // Fronts Only SKU mapping was confirmed and Step3New auto-selected Front Seats.
                    // For all direct Front Seats selections, use the first mapping's coverages.
                    biz2_coverages: keepCoverages ? (prev?.biz2_coverages ?? null) : biz2CoveragesFromMapping,
                    sku_mapping_availability: firstMappingAvailability ?? prev?.sku_mapping_availability ?? null,
                };
            });
        } else {
            const allAnswered = questions.every(q => selectedAnswers[q.id] !== undefined);
            if (!allAnswered) {
                setCurrentAppData((prev: AppDataType | null) => ({ ...prev, configurator_complete: false }));
            }
        }
    }, [isFrontsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset answers when seat coverage changes or when the seat step is re-opened
    // (handleClickChange in Step3New clears both seat and biz2_coverages at once).
    // year/make/model changes are handled separately via the [data] effect above.
    useEffect(() => {
        if (!questions.length) {
            // No questions for this vehicle — mark complete so seat selection immediately
            // advances to color, even after handleClickChange reset it to false.
            setCurrentAppData(prev =>
                prev?.configurator_complete === true ? prev : { ...prev, configurator_complete: true }
            );
            return;
        }
        // The [isFrontsOnly] effect handles this case — don't fight it.
        if (isFrontsOnly) return;
        // Don't reset when configurator already completed — the seat may have auto-changed
        // after a Fronts Only confirmation (compatibility → 'F' → Step3New picks Front Seats).
        if (currentAppData?.configurator_complete === true) return;
        // Don't reset when biz2_coverages was just set (answers saved, e.g. Fronts Only pending confirmation).
        if (currentAppData?.biz2_coverages != null) return;
        setSelectedAnswers({});
        setShowFrontsOnlyAlert(false);
        setExpandedQuestions(new Set([questions[0].id]));
        setUnlockedQuestions(new Set([questions[0].id]));
        setCurrentAppData(prev => ({ ...prev, configurator_complete: false, biz2_coverages: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAppData?.seat, currentAppData?.biz2_coverages]);

    // Reset and expand first question when configurator data changes
    useEffect(() => {
        setSelectedAnswers({});
        setCurrentAppData(prev => ({ ...prev, configurator_has_questions: questions.length > 0 }));
        if (questions.length > 0) {
            setExpandedQuestions(new Set([questions[0].id]));
            setUnlockedQuestions(new Set([questions[0].id]));
            // Configurator questions replace trim/cab — suppress them regardless of vehicle data
            setCurrentAppData(prev => ({
                ...prev,
                configurator_complete: false,
                biz2_coverages: null,
                has_trims: false,
                has_cabs: false,
                ...(window.scs_new_design_2026 && window.scs_biz2 ? { seat: '' } : {}),
            }));
        } else {
            setExpandedQuestions(new Set());
            setUnlockedQuestions(new Set());
            const firstMapping = configurator[0]?.sku_mappings?.[0];
            // Trim+cab responses with configurator:null carry availability and video
            // at data.availability / data.video rather than inside a sku_mapping.
            const topLevelVideo = (data?.data as any)?.video;
            const firstMappingAvail0 = firstMapping?.availability ?? data?.data?.availability ?? null;
            const firstBundleCode = firstMapping?.bundle_code ?? undefined;
            const firstVideo = firstMapping?.video ?? topLevelVideo;
            setCurrentAppData(prev => ({
                ...prev,
                configurator_complete: true,
                biz2_coverages: buildBiz2Coverages(firstMappingAvail0),
                bundle_code: firstBundleCode,
                video_url: firstVideo?.video_url ?? undefined,
                video_hero_img_url: firstVideo?.hero_img_url ?? undefined,
                video_hero_img_title: firstVideo?.hero_img_title ?? undefined,
            }));
        }
        const firstMappingAvail = configurator[0]?.sku_mappings?.[0]?.availability ?? null;
        const skuMappingAvailSource = firstMappingAvail ?? data?.data?.availability ?? null;
        if (skuMappingAvailSource) {
            setCurrentAppData(prev => ({ ...prev, sku_mapping_availability: normalizeAvailability(skuMappingAvailSource) }));
        }

        // If the availability response contains compatibility info (or even if it doesn't),
        // hard-override compatibility to 'HF' for configurator flow on 2002-2008 RAM 1500/2500/3500/4500/5500
        const isConfiguratorOverride = window?.scs_vehicle_configurator === true &&
            typeof year !== 'undefined' &&
            typeof make !== 'undefined' &&
            typeof model !== 'undefined' &&
            Number(year) >= 2002 && Number(year) <= 2008 &&
            String(make).toLowerCase() === 'ram' &&
            /(^|\D)(1500|2500|3500|4500|5500)(\D|$)/i.test(String(model));

        if (isConfiguratorOverride) {
            setCurrentAppData(prev => {
                if (!prev) return prev;
                const updatedCheck = { ...(prev.check ?? {}), compatibility: 'HF', compatibility_custom: (prev.check?.compatibility_custom ?? 'HF') };
                return { ...prev, check: updatedCheck };
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // In biz2 stepper: when a non-fronts seat is selected at step 2 and questions exist,
    // advance immediately to the questions step. Handles both manual selection and auto-selection,
    // and the race between questions data loading and seat being set.
    useEffect(() => {
        if (!window.scs_new_design_2026 || !window.scs_biz2) return;
        if (!questions.length || isFrontsOnly) return;
        if ((currentAppData?.step ?? 0) !== 2 || !currentAppData?.seat) return;
        setCurrentAppData(prev => ({ ...prev, step: 3 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAppData?.seat, questions.length]);

    // When all questions answered, find matching sku_mapping and build biz2_coverages
    useEffect(() => {
        if (questions.length === 0) return;
        const skuMappings = configurator[0]?.sku_mappings ?? [];
        const allAnswered = questions.every(q => selectedAnswers[q.id] !== undefined);
        if (!allAnswered) {
            setCurrentAppData(prev => ({ ...prev, configurator_complete: false }));
            const answeredEntries = Object.entries(selectedAnswers);
            if (answeredEntries.length > 0) {
                const surviving = skuMappings.filter(mapping =>
                    answeredEntries.every(([qId, key]) => mapping.conditions[String(qId)] === key)
                );
                if (surviving.length > 0 && surviving.every(m => m.bundle_code === 'Fronts Only')) {
                    setShowFrontsOnlyAlert(true);
                }
            }
            return;
        }

        const matched = skuMappings.find(mapping =>
            Object.entries(mapping.conditions).every(
                ([questionId, answerKey]) => selectedAnswers[Number(questionId)] === answerKey
            )
        );

        let biz2_coverages: Record<string, Record<string, { variant_id: number | null; available: boolean }>> | null = null;
        if (matched) {
            biz2_coverages = {};
            const normalized = normalizeAvailability(matched.availability ?? {});
            for (const [seat, seatData] of Object.entries(normalized)) {
                biz2_coverages[seat] = {};
                const prekit = Array.isArray((seatData as any).prekit) ? {} : (seatData as any).prekit ?? {};
                for (const [color, entry] of Object.entries(prekit)) {
                    biz2_coverages[seat][color] = {
                        variant_id: (entry as any)?.id ?? null,
                        available: (seatData as any)[color] === true,
                    };
                }
            }
        }

        const isFrontsOnlyMapping = matched?.bundle_code === 'Fronts Only';

        console.log('[VehicleConfigurator] matched SKU mapping', {
            bundle_code: matched?.bundle_code,
            isFrontsOnlyMapping,
            biz2_coverages_keys: biz2_coverages ? Object.keys(biz2_coverages) : null,
            biz2_coverages,
        });

        setCurrentAppData(prev => {
            if (!prev) return prev;

            let updatedCheck = prev.check;
            // Switching away from a Fronts Only mapping: restore the original compatibility.
            // The override to 'F' is applied later in the confirm handler, not here.
            if (!isFrontsOnlyMapping && originalCompatibilityRef.current !== null && prev.check) {
                updatedCheck = { ...prev.check, compatibility: originalCompatibilityRef.current };
                originalCompatibilityRef.current = null;
            }

            // For Fronts Only, don't mark complete yet — wait for user to confirm the alert.
            // Also don't change compatibility yet; that happens on confirm to avoid
            // triggering premature seat auto-selection before the user dismisses the dialog.
            return {
                ...prev,
                configurator_complete: !isFrontsOnlyMapping,
                biz2_coverages,
                sku_mapping_availability: matched?.availability ?? null,
                check: updatedCheck,
                bundle_code: matched?.bundle_code ?? undefined,
                video_url: matched?.video?.video_url ?? undefined,
                video_hero_img_url: matched?.video?.hero_img_url ?? undefined,
                video_hero_img_title: matched?.video?.hero_img_title ?? undefined,
            };
        });

        if (isFrontsOnlyMapping) {
            setShowFrontsOnlyAlert(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnswers]);

    // In biz2 stepper: when configurator becomes complete and a seat is already selected, advance:
    //   step 2 → step 3: no-questions vehicle (seat chosen, nothing pending)
    //   step 3 → step 4: questions answered (with biz2_coverages); or step 3 for Fronts Only mapping
    useEffect(() => {
        if (!window.scs_new_design_2026 || !window.scs_biz2) return;
        if (!currentAppData?.seat || currentAppData?.configurator_complete !== true) return;
        const isFrontsOnlyMapping = currentAppData?.check?.compatibility === 'F' && currentAppData?.biz2_coverages != null;
        if (currentAppData?.step === 2) {
            setCurrentAppData(prev => ({ ...prev, step: 3 }));
        } else if (currentAppData?.step === 3) {
            const nextStep = (currentAppData?.biz2_coverages != null && !isFrontsOnlyMapping) ? 4 : 3;
            setCurrentAppData(prev => ({ ...prev, step: nextStep }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAppData?.configurator_complete, currentAppData?.seat, currentAppData?.biz2_coverages]);

    const isBiz2Stepper = !!(window.scs_new_design_2026 && window.scs_biz2);
    // VehicleConfigurator is at step 3 in the biz2 stepper — shown after seat selection (step 2).
    const biz2ActiveStep = 3;

    // Don't render anything until we know questions exist for this vehicle.
    // Show a locked placeholder only while data is still loading (we don't know yet).
    if (!questions.length || isFrontsOnly) {
        if (isBiz2Stepper && isLoading && (currentAppData?.step ?? 0) < biz2ActiveStep) {
            return (
                <section className={`${sharedStyles.section__step} ${sharedStyles.section_locked} ${sharedStyles.scs_new_design_2026}`}>
                    <h2><span className={sharedStyles.section__step_no}>{stepNumber}</span></h2>
                    <div className={sharedStyles.selector_wrapper}>
                        <strong><span className={sharedStyles.section__step_no}>{stepNumber}</span>Rear Seat Configuration</strong>
                    </div>
                </section>
            );
        }

        // While the vehicle availability request is in flight, show a small inline loader
        // so the user sees feedback immediately after selecting model.
        if ((isLoading && enabled) || currentAppData?.isFetchingCheck) {
            return (
                <section className={`${sharedStyles.section__step} ${sharedStyles.scs_new_design_2026}`}>
                    <h2><span className={sharedStyles.section__step_no}>{stepNumber}</span></h2>
                    <div className={sharedStyles.selector_wrapper}>
                        <strong><span className={sharedStyles.section__step_no}>{stepNumber}</span>Rear Seat Configuration</strong>
                        <div style={{display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 12}}>
                            <span className={stepNavStyles.loader + ' ' + stepNavStyles.loader_black} aria-hidden="true"></span>
                            <span style={{fontSize: 14, color: '#000'}}>Loading configuration...</span>
                        </div>
                    </div>
                </section>
            );
        }

        return null;
    }

    // Questions exist but seat step hasn't completed yet — show locked placeholder.
    if (isBiz2Stepper && (currentAppData?.step ?? 0) < biz2ActiveStep) {
        return (
            <section className={`${sharedStyles.section__step} ${sharedStyles.section_locked} ${sharedStyles.scs_new_design_2026}`}>
                <h2><span className={sharedStyles.section__step_no}>{stepNumber}</span></h2>
                <div className={sharedStyles.selector_wrapper}>
                    <strong><span className={sharedStyles.section__step_no}>{stepNumber}</span>Rear Seat Configuration</strong>
                </div>
            </section>
        );
    }

    // In biz2 stepper: once configurator is complete AND a seat is chosen, show a collapsed summary.
    // The summary shows when step has advanced past the seat/config step (step 2).
    if (isBiz2Stepper && (currentAppData?.step ?? 0) > biz2ActiveStep) {
        const handleGoBack = () => {
            setSelectedAnswers({});
            setExpandedQuestions(new Set());
            setUnlockedQuestions(new Set());
            setShowFrontsOnlyAlert(false);
            setCurrentAppData(prev => {
                // Restore original compatibility if it was overridden for a Fronts Only mapping
                let updatedCheck = prev?.check;
                if (originalCompatibilityRef.current !== null && prev?.check) {
                    updatedCheck = { ...prev.check, compatibility: originalCompatibilityRef.current };
                    originalCompatibilityRef.current = null;
                }
                return { ...prev, step: biz2ActiveStep, configurator_complete: false, biz2_coverages: null, color: 'Black', addons: '', check: updatedCheck };
            });
        };
        return (
            <section className={`${sharedStyles.section__step} ${sharedStyles.scs_new_design_2026}`}>
                <h2><span className={`${sharedStyles.section__step_no} ${sharedStyles.section__step_no_done}`}>{stepNumber}</span></h2>
                <div className={sharedStyles.selector_wrapper}>
                    <strong><span className={`${sharedStyles.section__step_no} ${sharedStyles.section__step_no_done}`}>{stepNumber}</span>Rear Seat Configuration</strong>
                    <div className={sharedStyles.summary}>
                        <div className={sharedStyles.selected_value}>Configuration complete</div>
                        <button type="button" className={sharedStyles.btn_change} onClick={handleGoBack} aria-label="Edit rear seat configuration">
                            <svg width="14" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M0.274589 0.274547C0.640708 -0.0915156 1.2343 -0.0915156 1.60041 0.274547L7.1875 5.86167L12.7746 0.274547C13.1407 -0.0915156 13.7343 -0.0915156 14.1004 0.274547C14.4665 0.640672 14.4665 1.2343 14.1004 1.60042L7.85044 7.85042C7.48431 8.21648 6.89069 8.21648 6.52456 7.85042L0.274589 1.60042C-0.0915297 1.2343 -0.0915297 0.640672 0.274589 0.274547Z" fill="black"/></svg>
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    const skuMappings = configurator[0]?.sku_mappings ?? [];

    // Returns the set of valid answer keys for a question given the answers already selected
    // for all questions before it. Only keys that appear in at least one surviving sku_mapping are valid.
    const getValidAnswerKeys = (questionId: number, priorAnswers: Record<number, string>): Set<string> => {
        const surviving = skuMappings.filter(mapping =>
            Object.entries(priorAnswers).every(
                ([qId, key]) => mapping.conditions[qId] === key
            )
        );
        return new Set(surviving.map(m => m.conditions[String(questionId)]).filter(Boolean));
    };

    const handleSelectAnswer = (questionId: number, answerKey: string, questionIndex: number) => {
        setSelectedAnswers(prev => {
            // Clear answers for all subsequent questions when re-answering this one
            const next: Record<number, string> = {};
            questions.forEach((q, i) => {
                if (i < questionIndex) next[q.id] = prev[q.id];
            });
            next[questionId] = answerKey;
            return next;
        });
        // Collapse current question, lock/collapse all subsequent, unlock and expand next
        setExpandedQuestions(prev => {
            const next = new Set(prev);
            next.delete(questionId);
            questions.forEach((q, i) => { if (i > questionIndex) next.delete(q.id); });
            if (questionIndex + 1 < questions.length) {
                next.add(questions[questionIndex + 1].id);
            }
            return next;
        });
        setUnlockedQuestions(prev => {
            const next = new Set(prev);
            questions.forEach((q, i) => { if (i > questionIndex) next.delete(q.id); });
            if (questionIndex + 1 < questions.length) {
                next.add(questions[questionIndex + 1].id);
            }
            return next;
        });
    };

    const toggleQuestion = (questionId: number) => {
        if (!unlockedQuestions.has(questionId)) return;
        setExpandedQuestions(prev => {
            const next = new Set(prev);
            if (next.has(questionId)) {
                next.delete(questionId);
            } else {
                // Opening an already-answered question: close all subsequent open questions
                const questionIndex = questions.findIndex(q => q.id === questionId);
                questions.forEach((q, i) => { if (i > questionIndex) next.delete(q.id); });
                next.add(questionId);
            }
            return next;
        });
    };

    return (
        <section className={`${styles.configurator_section} ${sharedStyles.section__step} ${sharedStyles.scs_new_design_2026}`}>
            <h2><span className={sharedStyles.section__step_no}>{stepNumber}</span></h2>

            <div className={sharedStyles.selector_wrapper}>
                <strong><span className={sharedStyles.section__step_no}>{stepNumber}</span>{'Rear Seat Configuration'}</strong>


                {showFrontsOnlyAlert && (
                    <div className={styles.fronts_only_overlay}>
                        <div className={styles.fronts_only_dialog}>
                            <p>This Rear Seat Configuration is currently not available, please select an option to continue</p>
                            <button type="button" className={styles.fronts_only_confirm} onClick={() => {
                                setShowFrontsOnlyAlert(false);
                                setCurrentAppData(prev => {
                                    if (!prev?.check) return { ...prev, configurator_complete: true };
                                    // Save original compatibility before overriding to 'F'
                                    if (originalCompatibilityRef.current === null) {
                                        originalCompatibilityRef.current = prev.check.compatibility;
                                    }
                                    return {
                                        ...prev,
                                        configurator_complete: true,
                                        check: { ...prev.check, compatibility: 'F' },
                                    };
                                });
                            }}>Continue with Fronts Only</button>
                            <button type="button" className={styles.fronts_only_reset} onClick={() => {
                                setShowFrontsOnlyAlert(false);
                                const lastAnswered = [...questions].reverse().find(q => selectedAnswers[q.id] !== undefined);
                                if (!lastAnswered) return;
                                setSelectedAnswers(prev => {
                                    const next = { ...prev };
                                    delete next[lastAnswered.id];
                                    return next;
                                });
                                setExpandedQuestions(prev => new Set(prev).add(lastAnswered.id));
                                setUnlockedQuestions(prev => new Set(prev).add(lastAnswered.id));
                            }}>Select a different rear seat configuration</button>
                        </div>
                    </div>
                )}

                <div className={styles.questions_list}>
                {questions.map((question, index) => {
                    const isExpanded = expandedQuestions.has(question.id);
                    const selectedKey = selectedAnswers[question.id];
                    const selectedAnswer = selectedKey
                        ? question.answers.find(a => a.key === selectedKey)
                        : undefined;
                    const priorAnswers: Record<number, string> = {};
                    questions.slice(0, index).forEach(q => {
                        if (selectedAnswers[q.id] !== undefined) priorAnswers[q.id] = selectedAnswers[q.id];
                    });
                    const validKeys = getValidAnswerKeys(question.id, priorAnswers);
                    const filteredAnswers = question.answers.filter(a => validKeys.has(a.key));

                    return (
                        <div key={question.id} className={`${styles.question_item} ${isExpanded ? styles.expanded : ''} ${!unlockedQuestions.has(question.id) ? styles.locked : ''}`}>
                            <button
                                className={styles.question_header}
                                onClick={() => toggleQuestion(question.id)}
                                type="button"
                            >
                                <span
                                    className={styles.question_text}
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`Q${index + 1}. ${question.text}`) }}
                                />
                                <span className={`${styles.chevron} ${isExpanded ? styles.chevron_expanded : ''}`}>
                                    <svg width="14" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M0.274589 0.274547C0.640708 -0.0915156 1.2343 -0.0915156 1.60041 0.274547L7.1875 5.86167L12.7746 0.274547C13.1407 -0.0915156 13.7343 -0.0915156 14.1004 0.274547C14.4665 0.640672 14.4665 1.2343 14.1004 1.60042L7.85044 7.85042C7.48431 8.21648 6.89069 8.21648 6.52456 7.85042L0.274589 1.60042C-0.0915297 1.2343 -0.0915297 0.640672 0.274589 0.274547Z" fill="black"/>
                                    </svg>
                                </span>
                            </button>

                            {!isExpanded && selectedAnswer && (
                                <div className={styles.answer_summary}>
                                    {selectedAnswer.icon_url && (
                                        <img
                                            src={bumpIconVersion(selectedAnswer.icon_url)}
                                            alt={selectedAnswer.label}
                                            className={styles.answer_summary_icon}
                                        />
                                    )}
                                    <span className={styles.answer_summary_label}>{selectedAnswer.label}</span>
                                </div>
                            )}

                            {isExpanded && (
                                <div className={styles.answers_grid}>
                                    {filteredAnswers.map(answer => (
                                        <button
                                            key={answer.key}
                                            type="button"
                                            className={`${styles.answer_option} ${selectedKey === answer.key ? styles.selected : ''}`}
                                            onClick={() => handleSelectAnswer(question.id, answer.key, index)}
                                        >
                                            {answer.icon_url && (
                                                <img
                                                    src={bumpIconVersion(answer.icon_url)}
                                                    alt={answer.label}
                                                    className={styles.answer_icon}
                                                />
                                            )}
                                            <span className={styles.answer_label}>{answer.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            </div>

            
        </section>
    );
};

export default VehicleConfigurator;
